import { useState } from 'react';
import { X } from 'lucide-react';
import { palette } from '../../styles/tokens';
import type { PeakWindow } from '../../types';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TIME_OPTIONS: string[] = [];
for (let h = 0; h < 24; h++) for (const m of [0, 30]) {
  TIME_OPTIONS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
}

function fmtTime12(t: string) {
  const [hh, mm] = t.split(':').map(Number);
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h = hh % 12 || 12;
  return mm ? `${h}:${String(mm).padStart(2, '0')} ${ampm}` : `${h} ${ampm}`;
}

interface Props {
  label: string;
  sublabel: string;
  windows: PeakWindow[];
  onChange: (w: PeakWindow[]) => void;
}

export function TimeWindowEditor({ label, sublabel, windows, onChange }: Props) {
  const [addDay, setAddDay] = useState(6);
  const [addStart, setAddStart] = useState('09:00');
  const [addEnd, setAddEnd] = useState('12:00');
  const [open, setOpen] = useState(false);

  const handleAdd = () => {
    if (addStart >= addEnd) return;
    onChange([...windows, { day: addDay, start: addStart, end: addEnd }]);
  };

  const handleRemove = (i: number) => onChange(windows.filter((_, idx) => idx !== i));

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: palette.g600 }}>{label}</div>
          <div style={{ fontSize: 11, color: palette.g400 }}>{sublabel}</div>
        </div>
        <button onClick={() => setOpen(!open)} style={{
          fontSize: 11, fontWeight: 600, color: palette.accent, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
        }}>{open ? 'Done' : '+ Add'}</button>
      </div>

      {windows.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {windows.map((w, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, padding: '4px 10px', borderRadius: 12, background: palette.accentWash, color: palette.accentDeep, fontWeight: 500 }}>
              {DAY_LABELS[w.day]} {fmtTime12(w.start)} — {fmtTime12(w.end)}
              <X size={11} style={{ cursor: 'pointer', marginLeft: 2 }} onClick={() => handleRemove(i)} />
            </span>))}
        </div>
      )}

      {open && (() => {
        const SS: React.CSSProperties = { padding: '5px 8px', borderRadius: 5, border: `1px solid ${palette.g200}`, fontSize: 11, fontFamily: 'inherit', color: palette.g700 };
        return (<div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <select value={addDay} onChange={e => setAddDay(Number(e.target.value))} style={SS}>{DAY_LABELS.map((d, i) => <option key={i} value={i}>{d}</option>)}</select>
          <select value={addStart} onChange={e => setAddStart(e.target.value)} style={SS}>{TIME_OPTIONS.map(t => <option key={t} value={t}>{fmtTime12(t)}</option>)}</select>
          <span style={{ fontSize: 11, color: palette.g400 }}>to</span>
          <select value={addEnd} onChange={e => setAddEnd(e.target.value)} style={SS}>{TIME_OPTIONS.map(t => <option key={t} value={t}>{fmtTime12(t)}</option>)}</select>
          <button onClick={handleAdd} disabled={addStart >= addEnd} style={{ padding: '5px 12px', borderRadius: 5, border: 'none', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: addStart >= addEnd ? 'not-allowed' : 'pointer', background: addStart >= addEnd ? palette.g200 : palette.accent, color: palette.white }}>Add</button>
        </div>);
      })()}
    </div>
  );
}
